import { Search, Eye, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../../config/api";
import SimpleTable from "../../../Component/Common/SimpleTable";
import ViewButton from "../../../Component/Common/ViewButton";
import SearchInput from "../../../Component/Common/SearchInput";

// UI helpers
function StatusPill({ active }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] sm:text-xs font-semibold " +
        (active
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-rose-50 text-rose-700 border-rose-200")
      }
    >
      <span
        className={
          "h-1.5 w-1.5 rounded-full " + (active ? "bg-emerald-500" : "bg-rose-500")
        }
      />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function formatDate(isoLike) {
  if (!isoLike) return "—";
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

export default function CompanyView() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const fetchCompanies = async (page = 1) => {
  try {
    setLoading(true);

    const res = await axios.get(
      `${BASE_URL}common/get-users?role=2&page=${page}`
    );

    if (!res.data?.status) {
      toast.error(res.data?.message || "Failed to load companies");
      return;
    }

    // ✅ FIXED: use correct keys from response
    setCompanies(res.data.data || []);

    setCurrentPage(res.data.pagination?.current_page || 1);
    setTotalPages(res.data.pagination?.last_page || 1);
  } catch (e) {
    console.error(e);
    toast.error("Failed to load companies");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchCompanies(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const filteredCompanies = useMemo(() => {
    if (!debouncedSearch) return companies;
    const q = debouncedSearch.toLowerCase();
    return companies.filter((c) => {
      return (
        c.id?.toString().includes(q) ||
        c.business_name?.toLowerCase().includes(q) ||
        c.contact_person?.toLowerCase().includes(q) ||
        c.contact_phone?.includes(debouncedSearch) ||
        c.contact_email?.toLowerCase().includes(q)
      );
    });
  }, [companies, debouncedSearch]);

  const companyRows = filteredCompanies.map((c) => ({
  raw: c,
  cells: [
    // Company ID
    {
      title: `#${c.id}`,
    },

    // Company Name + Email
    {
      title: c.business_name || "—",
      sub: c.contact_email || "—",
    },

    // Enrollment Date
    {
      title: formatDate(c.created_at || c.enrollment_date),
    },

    // Status
    {
      title: <StatusPill active={c.status === 1} />,
    },
  ],
}));

  return (
    <div className="space-y-4">
      <SectionCard
        title="Company Directory & Overview"
        
      >

        <div className="flex justify-end items-stretch gap-2 mb-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              onClear={() => setSearch("")}
              placeholder="Search by id, name, phone, email…"
              size="md"
            />
            <div className="hidden lg:block">
              <RefreshButton onClick={() => fetchCompanies(currentPage)} />
            </div>
          </div>
        {/* Loader */}
        {loading && (
          <div className="flex justify-center items-center h-[50vh]">
            <RegularLoader />
          </div>
        )}

        {/* Empty */}
        {!loading && filteredCompanies.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
              <Search className="h-5 w-5" />
            </div>
            <div className="text-sm font-semibold text-slate-900">No companies found</div>
            <div className="text-xs text-slate-500 mt-1">Try changing your search keywords.</div>
          </div>
        )}

        {/* Table */}
        {!loading && filteredCompanies.length > 0 && (
          <>

          {/* MOBILE CARD VIEW */}
          <div className="md:hidden space-y-4">
            {filteredCompanies.map((c) => (
              <div
                key={c.id}
                className="relative bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 p-4"
              >
                {/* TOP ROW */}
                <div className="flex items-start justify-between">

                  {/* LEFT SIDE */}
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-semibold text-base shadow-md">
                      {c.business_name?.charAt(0)?.toUpperCase() || "C"}
                    </div>

                    {/* Name + Subtitle */}
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {c.business_name || "—"}
                      </div>

                      <div className="text-xs text-slate-500 mt-0.5">
                        {c.contact_person || c.contact_email || "Company"}
                      </div>

                      <div className="text-[11px] text-slate-400 mt-1">
                        Enrolled {formatDate(c.created_at || c.enrollment_date)}
                      </div>
                    </div>
                  </div>

                  {/* STATUS */}
                  <StatusPill active={c.status === 1} />
                </div>

                {/* ACTION BUTTON */}
                <div className="mt-4">
                  <button
                    onClick={() =>
                      navigate(`/user/${c.id}`, {
                        state: { entityType: "company", data: c },
                      })
                    }
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-2xl text-sm font-medium hover:bg-blue-700 transition shadow-sm"
                  >
                    <Eye size={16} />
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block">
           <SimpleTable
            headers={[
              "Company ID",
              "Company Name",
              "Date of Enrollment",
              "Status",
            ]}
            rows={companyRows}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            renderActions={(c) => (
              <ViewButton
                onClick={() =>
                  navigate(`/user/${c.id}`, {
                    state: { entityType: "company", data: c },
                  })
                }
              />
            )}
            showSrNo
            pageSize={10}
          />
          </div>
          </>
          
        )}
      </SectionCard>

      {/* Global CSS (copy into src/index.css or your global stylesheet)
          Keep Tailwind base/components/utilities already configured.
      */}
      <style>{`
        :root {
          --brand-blue: 37 99 235; /* tailwind blue-600 */
          --page-bg: 248 250 252; /* slate-50 */
        }

        body {
          background: rgb(var(--page-bg));
        }

        /* Nice scrollbar (optional) */
        *::-webkit-scrollbar { height: 10px; width: 10px; }
        *::-webkit-scrollbar-thumb { background: rgba(var(--brand-blue), .18); border-radius: 999px; }
        *::-webkit-scrollbar-track { background: rgba(148, 163, 184, .15); }
      `}</style>
    </div>
  );
}


// *********************************************Working Code*********************************************8


// import { Plus, Search } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Swal from "sweetalert2";
// import toast from "react-hot-toast";
// import Pagination from "../../../Component/Common/Pagination";
// import { BASE_URL } from "../../../config/api";

// export default function CompanyView() {
//   const navigate = useNavigate();

//   const [companies, setCompanies] = useState([]);
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");

//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   // Pagination state
//   const [pagination, setPagination] = useState({
//     current_page: 1,
//     last_page: 1,
//     next_page_url: null,
//     prev_page_url: null,
//   });

//   // Debounce search
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 300);
//     return () => clearTimeout(timer);
//   }, [search]);


//   // -------------------------
//   // Fetch Companies (NEW API)
//   // -------------------------
//   const fetchCompanies = async (page = 1) => {
//   try {
//     setLoading(true);

//     const response = await axios.get(
//       `${BASE_URL}common/get-users?role=2&page=${page}`
//     );

//     const apiData = response.data?.data || {};

//     setCompanies(apiData.data || []);
//     setFilteredCompanies(apiData.data || []);

//     setCurrentPage(apiData.current_page);
//     setTotalPages(apiData.last_page);

//   } catch (error) {
//     toast.error("Failed to load companies");
//   } finally {
//     setLoading(false);
//   }
// };


//   useEffect(() => {
//     fetchCompanies(currentPage);
//   }, [currentPage]);


//   // -------------------------
//   // Search filter
//   // -------------------------
//   useEffect(() => {
//     const filtered = companies.filter((c) =>
//       c.business_name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
//       c.contact_person?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
//       c.contact_phone?.includes(debouncedSearch)
//     );

//     setFilteredCompanies(filtered);
//   }, [debouncedSearch, companies]);


//   // -------------------------
//   // Toggle Status
//   // -------------------------
//   const toggleStatus = async (company) => {
//     const newStatus = company.status === 1 ? 0 : 1;

//     const result = await Swal.fire({
//       title: "Are you sure?",
//       text: `You want to ${newStatus === 1 ? "activate" : "deactivate"} this company?`,
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonColor: "#2563eb",
//       cancelButtonColor: "#d33",
//       confirmButtonText: "Yes",
//     });

//     if (!result.isConfirmed) return;

//     try {
//       const response = await axios.post(
//         `${BASE_URL}/company/status/${company.id}`,
//         { status: newStatus }
//       );

//       if (response.data?.status) {
//         toast.success("Status updated successfully");
//         fetchCompanies(pagination.current_page);
//       } else {
//         toast.error("Failed to update status");
//       }
//     } catch (error) {
//       toast.error("Something went wrong");
//     }
//   };


//   return (
//     <div className="space-y-4">
//       <SectionCard title="Company Directory & Overview">

//         {/* Search & Actions */}
//         <div className="flex justify-end gap-2 items-center mb-2">
//           <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-4 h-4 text-slate-800" />
//             <input
//               type="text"
//               placeholder="Search company..."
//               className="bg-transparent text-xs outline-none placeholder:text-slate-700"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//             />
//           </div>

//           {/* <BlueButton
//             onClick={() => navigate("/add-entity/company")}
//             className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//           >
//             <Plus size={14} className="mr-1" /> Add
//           </BlueButton> */}

//           <RefreshButton onClick={() => fetchCompanies(pagination.current_page)} />
//         </div>

//         {/* Loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* No Results */}
//         {!loading && filteredCompanies.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">
//             No companies found.
//           </p>
//         )}

//         {/* Table */}
//         {!loading && filteredCompanies.length > 0 && (
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-[10px] sm:text-sm text-center">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 px-2">Company</th>
//                   <th className="py-2 px-2">Contact</th>
//                   <th className="py-2 px-2">PAN</th>
//                   <th className="py-2 px-2">Status</th>
//                   <th className="py-2 px-2">Actions</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {filteredCompanies.map((c) => (
//                   <tr key={c.id} className="border-b border-slate-50 hover:bg-blue-50/50">
//                     <td className="py-2 px-2 font-semibold">{c.business_name || "—"}</td>

//                     <td className="py-2 px-2 whitespace-nowrap">
//                       <div>{c.contact_phone || "--"}</div>
//                       <div className="text-[9px] text-slate-500">{c.contact_email || "--"}</div>
//                     </td>

//                     <td className="py-2 px-2">{c.pan || "—"}</td>

//                     <td className="py-2 px-2">
//                       <button
//                         onClick={() => toggleStatus(c)}
//                         className={`px-2 py-1 rounded text-[9px] ${
//                           c.status === 1
//                             ? "bg-green-50 text-green-700 border border-green-200"
//                             : "bg-red-50 text-red-700 border border-red-200"
//                         }`}
//                       >
//                         {c.status === 1 ? "Active" : "Inactive"}
//                       </button>
//                     </td>

//                     <td className="py-2 px-2">
//                       <div className="flex justify-center gap-2">
//                         <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-blue-50 text-blue-700 hover:bg-blue-100">
//                           View
//                         </button>

//                         <BlueButton
//                           className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
//                           onClick={() => navigate(`/assign-products/${c.id}`, { state: { c } })}
//                         >
//                           Assign Product
//                         </BlueButton>
//                       </div>
//                     </td>

//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}

//         {/* Pagination Buttons */}
//         {!loading && filteredCompanies.length > 0 && totalPages > 1 && (
//           <div className="mt-4 flex justify-center">
//             <Pagination
//               currentPage={currentPage}
//               totalPages={totalPages}
//               onPageChange={(page) => setCurrentPage(page)}
//             />
//           </div>
//         )}

//       </SectionCard>
//     </div>
//   );
// }



// import { Plus, Search } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Swal from "sweetalert2";
// import toast from "react-hot-toast";

// export default function CompanyView() {
//   const navigate = useNavigate();
//   const [companies, setCompanies] = useState([]);
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");

//   // Debounce search
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 300);
//     return () => clearTimeout(timer);
//   }, [search]);

//   const fetchCompanies = async () => {
//     try {
//       setLoading(true);

//       const response = await axios.get(
//         "${BASE_URL}/company/list",
//         { headers: { "Content-Type": "application/json" } }
//       );

//       if (response.data?.status) {
//         setCompanies(response.data.data || []);
//         setFilteredCompanies(response.data.data || []);
//       }

//     } catch (error) {
//       console.error("Error fetching companies:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Filter search
//   // useEffect(() => {
//   //   const filtered = companies.filter((c) =>
//   //     c.company_name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
//   //     c.contact_person?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
//   //     c.contact_phone?.includes(debouncedSearch)
//   //   );
//   //   setFilteredCompanies(filtered);
//   // }, [debouncedSearch, companies]);

//   // Initial fetch
//   useEffect(() => {
//     fetchCompanies();
//   }, []);

//   // -------------------------
//   // 🔥 Toggle Active / Inactive
//   // -------------------------
//   const toggleStatus = async (company) => {
//   const newStatus = company.status === 1 ? 0 : 1;

//   const result = await Swal.fire({
//     title: "Are you sure?",
//     text: `You want to ${newStatus === 1 ? "activate" : "deactivate"} this company?`,
//     icon: "warning",
//     showCancelButton: true,
//     confirmButtonColor: "#2563eb",
//     cancelButtonColor: "#d33",
//     confirmButtonText: "Yes",
//   });

//   if (!result.isConfirmed) return;

//   try {
//     const response = await axios.post(
//       `${BASE_URL}/company/status/${company.id}`,
//       { status: newStatus }
//     );

//     if (response.data?.status) {
//       toast.success("Status updated successfully");   // ✅ using react-hot-toast
//       fetchCompanies();
//     } else {
//       toast.error("Failed to update status");         // ❌ error toast
//     }
//   } catch (error) {
//     toast.error("Something went wrong");              // ❌ error toast
//   }
// };


//   return (
//     <div className="space-y-4">
//       <SectionCard title="Company Directory & Overview">

//         {/* Search & Actions */}
//         <div className="flex justify-end gap-2 items-center mb-2">
//           <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-4 h-4 text-slate-800" />
//             <input
//               type="text"
//               placeholder="Search company..."
//               className="bg-transparent text-xs outline-none placeholder:text-slate-700"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//             />
//           </div>

//           <BlueButton
//             onClick={() => navigate("/add-entity/company")}
//             className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//           >
//             <Plus size={14} className="mr-1" /> Add
//           </BlueButton>

//           <RefreshButton onClick={fetchCompanies} />
//         </div>

//         {/* Loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* No Results */}
//         {!loading && filteredCompanies.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">
//             No matching companies found.
//           </p>
//         )}

//         {/* Table */}
//         {!loading && filteredCompanies.length > 0 && (
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-[10px] sm:text-sm text-center">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 px-2">Company</th>
//                   <th className="py-2 px-2">Region</th>
//                   <th className="py-2 px-2">Contact</th>
//                   <th className="py-2 px-2">PAN</th>
//                   <th className="py-2 px-2">Business Type</th>
//                   <th className="py-2 px-2">Status</th>
//                   <th className="py-2 px-2">Actions</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {filteredCompanies.map((c) => (
//                   <tr key={c.id} className="border-b border-slate-50 hover:bg-blue-50/50">
//                     <td className="py-2 px-2 font-semibold">{c.company_name}</td>

//                     <td className="py-2 px-2 whitespace-nowrap">
//                       {c.city}, {c.state}
//                     </td>

//                     <td className="py-2 px-2 whitespace-nowrap">
//                       <div>{c.contact_phone || "--"}</div>
//                       <div className="text-[9px] text-slate-500">{c.contact_email}</div>
//                     </td>

//                     <td className="py-2 px-2">{c.pan || "—"}</td>
//                     <td className="py-2 px-2">{c.business_type || "—"}</td>

//                     {/* Status */}
//                     <td className="py-2 px-2">
//                       <button
//                         onClick={() => toggleStatus(c)}
//                         className={`px-2 py-1 rounded text-[9px] ${
//                           c.status === 1
//                             ? "bg-green-50 text-green-700 border border-green-200"
//                             : "bg-red-50 text-red-700 border border-red-200"
//                         }`}
//                       >
//                         {c.status === 1 ? "Active" : "Inactive"}
//                       </button>
//                     </td>

//                     {/* Action Buttons */}
//                     <td className="py-2 px-2 flex justify-center gap-1">
//                       <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-blue-50 text-blue-700 hover:bg-blue-100">
//                         View
//                       </button>

//                       <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-slate-50 text-slate-700 hover:bg-slate-100">
//                         Download
//                       </button>
//                     </td>

//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }



// import { Plus, Search } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";

// export default function CompanyView() {
//   const navigate = useNavigate();
//   const [companies, setCompanies] = useState([]);
//   const [filteredCompanies, setFilteredCompanies] = useState([]);
//   const [loading, setLoading] = useState(false);

//   // Search
//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");

//   // Debounce search
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 300);
//     return () => clearTimeout(timer);
//   }, [search]);

//   const fetchCompanies = async () => {
//     try {
//       setLoading(true);

//       const response = await axios.get(
//         "${BASE_URL}/company/list",
//         { headers: { "Content-Type": "application/json" } }
//       );

//       if (response.data?.status) {
//         setCompanies(response.data.data || []);
//         setFilteredCompanies(response.data.data || []);
//       }

//     } catch (error) {
//       console.error("Error fetching companies:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Filter when search changes
//   useEffect(() => {
//     const filtered = companies.filter((c) =>
//       c.company_name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
//       c.contact_person?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
//       c.contact_phone?.includes(debouncedSearch)
//     );
//     setFilteredCompanies(filtered);
//   }, [debouncedSearch, companies]);

//   // Initial Fetch
//   useEffect(() => {
//     fetchCompanies();
//   }, []);

//   return (
//     <div className="space-y-4">
//       <SectionCard title="Company Directory & Overview">

//         {/* Search + Actions */}
//         <div className="flex justify-end gap-2 items-center mb-2">
//           <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-4 h-4 text-slate-800" />
//             <input
//               type="text"
//               placeholder="Search company..."
//               className="bg-transparent text-xs outline-none placeholder:text-slate-700"
//               value={search}
//               onChange={(e) => setSearch(e.target.value)}
//             />
//           </div>

//           <BlueButton
//             onClick={() => navigate("/add-entity/company")}
//             className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//           >
//             <Plus size={14} className="mr-1" /> Add
//           </BlueButton>

//           <RefreshButton onClick={fetchCompanies} />
//         </div>

//         {/* Loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* No Result */}
//         {!loading && filteredCompanies.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">
//             No matching companies found.
//           </p>
//         )}

//         {/* Table */}
//         {!loading && filteredCompanies.length > 0 && (
//           <div className="overflow-x-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-blue-300">
//             <table className="min-w-full text-[10px] sm:text-sm text-center">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500">
//                   <th className="py-2 px-2">Company</th>
//                   <th className="py-2 px-2">Region</th>
//                   <th className="py-2 px-2">Contact</th>
//                   <th className="py-2 px-2">PAN</th>
//                   <th className="py-2 px-2">Business Type</th>
//                   <th className="py-2 px-2">Status</th>
//                   <th className="py-2 px-2">Actions</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {filteredCompanies.map((c) => (
//                   <tr key={c.id} className="border-b border-slate-50 hover:bg-blue-50/50">
//                     <td className="py-2 px-2 font-semibold">{c.company_name}</td>
//                     <td className="py-2 px-2 whitespace-nowrap">{c.city}, {c.state}</td>
//                     <td className="py-2 px-2 whitespace-nowrap">
//                       <div>{c.contact_phone || "--"}</div>
//                       <div className="text-[9px] text-slate-500">{c.contact_email}</div>
//                     </td>
//                     <td className="py-2 px-2">{c.pan || "—"}</td>
//                     <td className="py-2 px-2">{c.business_type || "—"}</td>

//                     <td className="py-2 px-2">
//                       <span
//                         className={`px-2 py-1 rounded text-[9px] ${
//                           c.status === 1
//                             ? "bg-green-50 text-green-700 border border-green-200"
//                             : "bg-red-50 text-red-700 border border-red-200"
//                         }`}
//                       >
//                         {c.status === 1 ? "Active" : "Inactive"}
//                       </span>
//                     </td>

//                     <td className="py-2 px-2 flex justify-center gap-1">
//                       <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-blue-50 text-blue-700 hover:bg-blue-100">
//                         View
//                       </button>
//                       <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-slate-50 text-slate-700 hover:bg-slate-100">
//                         Download
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }



// import { Plus, Search } from "lucide-react";
// import { useEffect, useState } from "react";
// import axios from "axios";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import Pagination from "../../../Component/Common/Pagination";

// export default function CompanyView() {

//   const navigate = useNavigate();
//   const [companies, setCompanies] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);

//   // Search
//   const [search, setSearch] = useState("");
//   const [debouncedSearch, setDebouncedSearch] = useState("");

//   // User session
//   const userData = JSON.parse(localStorage.getItem("authUserData"));
//   const companyId = userData?.id;

//   // Debounce search
//   useEffect(() => {
//     const timer = setTimeout(() => setDebouncedSearch(search), 400);
//     return () => clearTimeout(timer);
//   }, [search]);


//   const fetchCompanies = async () => {
//     if (!companyId) return;

//     try {
//       setLoading(true);

//       const response = await axios.post(
//         "${BASE_URL}/company/all",
//         { company_id: companyId, search: debouncedSearch, page: currentPage },
//         { headers: { "Content-Type": "application/json" } }
//       );

//       if (response.data?.status) {
//         setCompanies(response.data.data || []);
//         setTotalPages(response.data?.meta?.last_page || 1);
//       }

//     } catch (error) {
//       console.error("Error fetching companies:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Fetch on page change or search
//   useEffect(() => {
//     fetchCompanies();
//   }, [currentPage, debouncedSearch]);


//   return (
//     <div className="space-y-4">
//       <SectionCard title="Company Directory & Overview">

//         {/* Search and Actions */}
//         <div className="flex justify-end gap-2 items-center mb-2">
          
//           {/* Search Box */}
//           <div className="flex items-center gap-1 px-3 py-1 rounded-xl border border-slate-200 bg-slate-50">
//             <Search className="w-4 h-4 text-slate-800" />
//             <input
//               type="text"
//               placeholder="Search company..."
//               className="bg-transparent text-xs outline-none placeholder:text-slate-700"
//               value={search}
//               onChange={(e) => {
//                 setSearch(e.target.value);
//                 setCurrentPage(1);
//               }}
//             />
//           </div>

//           <div className="flex gap-2">
//             {/* Add new company */}
//             <BlueButton
//               onClick={() => navigate("/add-entity/company")}
//               className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//             >
//               <Plus size={14} className="mr-1" /> Add
//             </BlueButton>

//             <RefreshButton onClick={fetchCompanies} />
//           </div>
//         </div>

//         {/* Loader */}
//         {loading && (
//           <div className="flex justify-center items-center h-[50vh]">
//             <RegularLoader />
//           </div>
//         )}

//         {/* No Result */}
//         {!loading && companies.length === 0 && (
//           <p className="text-center text-sm text-slate-500 py-3">
//             No matching companies found.
//           </p>
//         )}

//         {/* Table */}
//         {!loading && companies.length > 0 && (
//           <>
//             <div className="overflow-x-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-blue-300">
//               <table className="min-w-full text-[10px] sm:text-sm text-center">
//                 <thead>
//                   <tr className="border-b border-slate-100 text-slate-500">
//                     <th className="py-2 px-2">Company</th>
//                     <th className="py-2 px-2">Region</th>
//                     <th className="py-2 px-2">Contact</th>
//                     <th className="py-2 px-2">Employees</th>
//                     <th className="py-2 px-2">Policies</th>
//                     <th className="py-2 px-2">Status</th>
//                     <th className="py-2 px-2">Actions</th>
//                   </tr>
//                 </thead>

//                 <tbody>
//                   {companies.map((c) => (
//                     <tr key={c.id} className="border-b border-slate-50 hover:bg-blue-50/50">
                      
//                       {/* Company Name */}
//                       <td className="py-2 px-2">
//                         <div className="font-semibold">{c.company_name}</div>
//                         <div className="text-[9px] text-slate-500">{c.company_code}</div>
//                       </td>

//                       {/* Location */}
//                       <td className="py-2 px-2 whitespace-nowrap">
//                         {c.city}, {c.state}
//                       </td>

//                       {/* Contact */}
//                       <td className="py-2 px-2 whitespace-nowrap">
//                         <div>{c.phone || "--"}</div>
//                         <div className="text-[9px] text-slate-500">{c.email}</div>
//                       </td>

//                       {/* Employee Count  (Static example — replace if backend gives real numbers) */}
//                       <td className="py-2 px-2">
//                         {c.total_agents || "—"}
//                       </td>

//                       {/* Policy Stats */}
//                       <td className="py-2 px-2">
//                         {c.total_policy || "—"}
//                       </td>

//                       {/* Status */}
//                       <td className="py-2 px-2">
//                         <span className={`px-2 py-1 rounded text-[9px] ${
//                           c.status === 1 
//                             ? "bg-green-50 text-green-700 border border-green-200"
//                             : "bg-red-50 text-red-700 border border-red-200"
//                         }`}>
//                           {c.status === 1 ? "Active" : "Inactive"}
//                         </span>
//                       </td>

//                       {/* Actions */}
//                       <td className="py-2 px-2 flex flex-col sm:flex-row justify-center gap-1">
//                         <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-blue-50 text-blue-700 hover:bg-blue-100">
//                           View
//                         </button>
//                         <button className="text-[9px] sm:text-xs px-2 py-1 border rounded bg-slate-50 text-slate-700 hover:bg-slate-100">
//                           Download
//                         </button>
//                       </td>

//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>

//             {/* Pagination */}
//             {totalPages > 1 && (
//               <Pagination
//                 currentPage={currentPage}
//                 totalPages={totalPages}
//                 onPageChange={setCurrentPage}
//               />
//             )}
//           </>
//         )}

//       </SectionCard>
//     </div>
//   );
// }
